import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPLIER } from '@manpower/shared';
import { useAuth } from '@/auth/AuthProvider';
import { BrandMark, Button } from '@/components/primitives';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { PublicNavBar, PublicNavDrawer } from './PublicNav';
import { SignInMenu } from './SignInMenu';
import { DensityProvider } from '@/design/density';

/** One labelled column of footer links. */
function FooterColumn({
  heading,
  links,
}: {
  heading: string;
  links: readonly { to: string; label: string }[];
}) {
  return (
    <div>
      <h2 className="text-xs font-semibold uppercase tracking-wide text-white/70">{heading}</h2>
      <ul className="mt-3 space-y-2 text-sm text-[#b8c2bb]">
        {links.map((link) => (
          <li key={link.to}>
            <Link to={link.to} className="transition-colors duration-150 hover:text-[#a9ddc1]">
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

const HOME_FOR_ROLE = {
  SUPER_ADMIN: '/admin',
  ADMIN: '/admin',
  STAFF: '/admin',
  EMPLOYER: '/employer',
  SEEKER: '/seeker',
} as const;

/** Chrome for anonymous pages: home, job search, job detail, legal pages. */
export function PublicShell() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // Second argument is the English fallback, so an untranslated key renders a
  // real word rather than "nav.findJobs".
  const { t } = useTranslation();

  return (
    <DensityProvider density="public">
      <div className="flex min-h-screen flex-col bg-white">
        {/* Keyboard users should not have to tab the whole header to reach a
            job listing. Visible only on focus. */}
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-brand-600 focus:px-4 focus:py-2 focus:text-white"
        >
          Skip to content
        </a>
        <header className="sticky top-0 z-20 border-b border-ink-200 bg-white">
          <div className="mx-auto flex h-16 max-w-7xl items-center gap-6 px-4">
            <Link to="/" className="flex shrink-0 items-center gap-2.5">
              <BrandMark className="h-9 w-9 shrink-0" />
              <span className="hidden leading-tight sm:block">
                <span className="block text-sm font-semibold text-ink-900">
                  {SUPPLIER.brandShort}
                </span>
                <span className="block text-[11px] text-ink-500">{SUPPLIER.brandDescriptor}</span>
              </span>
            </Link>

            <PublicNavBar />

            <div className="ml-auto flex items-center gap-2">
              {/* Before sign-in, not after: a candidate who cannot read English
                  has to be able to change language on the page they land on. */}
              <LanguageSwitcher />
              {user ? (
                <Link to={HOME_FOR_ROLE[user.role]}>
                  <Button size="sm">{t('nav.dashboard', 'Go to dashboard')}</Button>
                </Link>
              ) : (
                <>
                  {/* Two controls, not two equals. Signing in is a return
                      journey for somebody who already knows why they came, so
                      it stays outlined; "Hire workers" is the only place on the
                      page where a stranger commits to anything, so it is the
                      only filled button — and orange, because registering a
                      company is one of the few commitments `action` is for.

                      "For employers" used to sit here as a third control while
                      the nav already carried a menu of that name, three inches
                      away. One name on two controls is a coin toss. */}
                  <SignInMenu />
                  <Link to="/employer/signup" className="hidden sm:block">
                    <Button variant="commit" size="sm">
                      {t('nav.hireWorkers', 'Hire workers')}
                    </Button>
                  </Link>
                </>
              )}
              <PublicNavDrawer />
            </div>
          </div>
        </header>

        {/* Advance-fee fraud is endemic in Indian blue-collar recruitment, so this
            sits above the fold on every page rather than in the footer, where the
            people it protects will never scroll to it. */}
        {/* Translated because it is the site's defence against advance-fee
            fraud. Leaving it in English protects only the people who were never
            at risk. */}
        <p className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-xs text-amber-900">
          <strong>{t('home.noFeeBanner', 'We never charge job seekers any fee, at any stage.')}</strong>{' '}
          {t(
            'home.noFeeDetail',
            'If anyone asks you for money in our name, do not pay — report it on',
          )}{' '}
          {/* Points at the report page rather than a phone number. The numbers
              came off the public site, and this banner is the one thing on it
              that a person already being defrauded will act on — leaving it
              pointing at nothing would have removed the site's defence while
              keeping its promise. */}
          <Link to="/report-fraud" className="font-medium underline">
            {t('nav.reportFraud', 'Report fraud')}
          </Link>
          .
        </p>

        <main id="main" className="flex-1">
          {/* Keyed on the path so React remounts this wrapper on every
              navigation and the animation replays. A SPA route change is
              otherwise an instant swap with no sense of having moved. */}
          <div key={pathname} className="motion-safe:animate-fade-in">
            <Outlet />
          </div>
        </main>

        <footer className="bg-footer text-white">
          <div className="mx-auto max-w-7xl px-4 py-10">
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr]">
              {/* Identity of the operating entity. An Indian job platform is
                  expected to name the company behind it — and a candidate being
                  asked for money can only check who is real if the legal name,
                  address and CIN are on the page. */}
              <div>
                <div className="flex items-center gap-2.5">
                  <BrandMark className="h-9 w-9 shrink-0" onDark />
                  <span className="leading-tight">
                    <span className="block text-sm font-semibold text-white">
                      {SUPPLIER.brandShort}
                    </span>
                    <span className="block text-[11px] text-[#b8c2bb]">
                      {SUPPLIER.brandDescriptor}
                    </span>
                  </span>
                </div>
                <p className="mt-3 text-sm font-medium text-[#b8c2bb]">
                  Reliable workforce for manufacturing excellence
                </p>
              </div>

              {/* Three link columns, split by who is reading rather than by
                  what kind of page it is. A candidate looking for the safety
                  guide should not have to scan past invoicing links to find it,
                  and "Report fraud" earns a place in the footer of every page
                  for the same reason the banner sits at the top of one. */}
              <FooterColumn
                heading={t('nav.forSeekers', 'Job seekers')}
                links={[
                  { to: '/jobs', label: t('nav.findJobs', 'Find jobs') },
                  { to: '/job-seekers', label: 'How it works' },
                  { to: '/companies', label: t('nav.companies', 'Companies') },
                  { to: '/safety', label: 'Staying safe' },
                  { to: '/job-seeker-terms', label: 'Job seeker terms' },
                ]}
              />

              <FooterColumn
                heading={t('nav.forEmployers', 'For employers')}
                links={[
                  { to: '/employers', label: 'Hire workers' },
                  { to: '/services', label: 'Services' },
                  { to: '/employer/signup', label: 'Register your company' },
                  { to: '/employer-verification', label: 'Employer verification' },
                  { to: '/employer-terms', label: 'Employer terms' },
                ]}
              />

              <FooterColumn
                heading="Support & legal"
                links={[
                  { to: '/contact', label: 'Contact us' },
                  { to: '/faq', label: 'FAQ' },
                  { to: '/report-fraud', label: 'Report fraud' },
                  { to: '/about', label: 'About us' },
                  { to: '/privacy-policy', label: 'Privacy policy' },
                  { to: '/privacy-choices', label: 'Your privacy choices' },
                  { to: '/terms', label: 'Terms of use' },
                  { to: '/accessibility', label: 'Accessibility' },
                ]}
              />
            </div>


            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-1 border-t border-white/15 pt-6 text-xs text-[#b8c2bb]">
              <span>
                © {new Date().getFullYear()} {SUPPLIER.brandName}. All rights reserved.
              </span>
            </div>
          </div>
        </footer>
      </div>
    </DensityProvider>
  );
}
